// libraryReturn.js — Item #7 (overnight 2026-07-27): the library ⇄ agent-shell RETURN handoff.
//
// "View all" from the agent experience navigates to the Task Library as a separate Rails page load.
// Coming BACK must restore the exact agent-shell state the user left: the open chat (left pane) and
// the active right-panel view (Board or Agent Jobs). Neither click site can see both values at click
// time, so this module carries them across the page loads (model: libraryLaunch.js):
//
//   1. ACTIVE_CHAT_MIRROR_KEY — AgentStudioChat mirrors its activeChat id into sessionStorage so the
//      panel-side click site (TriggeredTasksList, a different React subtree) can read it at click time.
//   2. LIBRARY_RETURN_KEY     — the View-all click sites stage {chatId, activityView} here, then
//      navigate to the library. LibraryPage CONSUMES it on mount (read+remove — a stale descriptor
//      can never resurface a back button on a later organic visit) and shows the back button only
//      when a descriptor was present.
//   3. LIBRARY_RESTORE_KEY    — the back button re-stages the descriptor here and navigates to the
//      FLAT /activity?mode=agent (the only route that mounts the shell). AgentExperienceShell
//      consumes it on mount and restores: activityView via onSelectView, then — ONLY when chatId is
//      non-null — re-dispatches the frozen id-only 'vesta:nav:select-chat' contract. It must NOT
//      emit the 'vesta:nav:chat-selected' meta companion (wrong channel; the chat would not restore).
//
// Pure over a Storage-like object so it's node-testable and never throws (malformed/absent → null).

import { isActivityHostPath } from "./modeToggleNav.js"

export const ACTIVE_CHAT_MIRROR_KEY = "vesta-active-chat-id"
export const LIBRARY_RETURN_KEY = "vesta-library-return"
export const LIBRARY_RESTORE_KEY = "vesta-library-restore"

// Item #7 taste round (2026-07-28), finding (ii): the agent sidebar (VsNavLower) renders its chat
// list on EVERY agent-chrome page, but only the activity route hosts the chat surface — on the
// library a chat click dead-dispatched into nothing (responsive-looking, did nothing). On a
// non-host page the click must NAVIGATE: the caller mirrors the clicked chat id (overriding
// whatever chat the stored return descriptor pointed at) and assigns this target; the chat
// surface's mount-time mirror restore then opens exactly that chat. Returns null on a host page
// (dispatch normally — no navigation).
export function sidebarChatNavTarget(pathname) {
  return isActivityHostPath(pathname) ? null : "/activity?mode=agent"
}

// The agent-mode library href: slug-scoped when we have a slug, flat otherwise (mirrors the library
// route pair). ?mode=agent is what keeps the server-rendered side nav in agent chrome on the library
// page (allowlisted in _side_nav.html.haml + honored by LibraryController like ActivityController).
export function agentLibraryHref(companySlug) {
  const base = companySlug ? `/companies/${companySlug}/library` : "/library"
  return `${base}?mode=agent`
}

// ── activeChat mirror (produce side, AgentStudioChat) ────────────────────────
export function mirrorActiveChat(storage, id) {
  if (!storage) return false
  try {
    if (id == null) storage.removeItem(ACTIVE_CHAT_MIRROR_KEY)
    else storage.setItem(ACTIVE_CHAT_MIRROR_KEY, String(id))
    return true
  } catch (_e) {
    return false
  }
}

export function readActiveChatId(storage) {
  if (!storage) return null
  try {
    const raw = storage.getItem(ACTIVE_CHAT_MIRROR_KEY)
    return raw == null || raw === "" ? null : String(raw)
  } catch (_e) {
    return null
  }
}

// ── descriptor plumbing ──────────────────────────────────────────────────────
// Descriptor shape: { chatId: String|null, activityView: 'board'|'agents' }.
function normalizeDescriptor(desc) {
  if (!desc || typeof desc !== "object") return null
  const activityView = desc.activityView === "agents" || desc.activityView === "board" ? desc.activityView : null
  if (!activityView) return null
  const chatId = desc.chatId == null || desc.chatId === "" ? null : String(desc.chatId)
  return { chatId, activityView }
}

function stageAt(storage, key, descriptor) {
  const desc = normalizeDescriptor(descriptor)
  if (!storage || !desc) return false
  try {
    storage.setItem(key, JSON.stringify(desc))
    return true
  } catch (_e) {
    return false
  }
}

// Read + REMOVE + parse. The REMOVE is unconditional-on-read so a back/forward navigation can never
// re-fire a restore (same rule as libraryLaunch.consumeLaunch).
function consumeAt(storage, key) {
  if (!storage) return null
  let raw = null
  try {
    raw = storage.getItem(key)
    if (raw != null) storage.removeItem(key)
  } catch (_e) {
    return null
  }
  if (!raw) return null
  try {
    return normalizeDescriptor(JSON.parse(raw))
  } catch (_e) {
    return null
  }
}

// View-all click sites → LibraryPage.
export function stageReturn(storage, descriptor) { return stageAt(storage, LIBRARY_RETURN_KEY, descriptor) }
export function consumeReturn(storage) { return consumeAt(storage, LIBRARY_RETURN_KEY) }

// LibraryPage back button → AgentExperienceShell.
export function stageRestore(storage, descriptor) { return stageAt(storage, LIBRARY_RESTORE_KEY, descriptor) }
export function consumeRestore(storage) { return consumeAt(storage, LIBRARY_RESTORE_KEY) }
